/**
 * Chips component for /search/tasks page only.
 * Uses TaskFilterUtilities (unit/candidate aggregate) and supports chip click-to-toggle and delete.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { ChipsContainer } from 'app/ComponentLibrary/Filter';
import { instanceOfISelection } from 'app/ComponentLibrary/Filter/CustomSelect';
import { filterAttributesBelongToGrid } from 'app/components/Common/Grid/GridStateManagement/gridFilterOwnership';
import { selectFilters } from 'app/components/Common/Grid/GridStateManagement/GridState.selectors';
import { generateTaskChips, TaskSearchFilterName } from '../../Tasks/TaskManagement/TaskFilters/TaskFilterUtilities';
import { initialCandidateAggregateSearch } from 'app/ComponentLibrary/Filter/CandidateSearch';
import { initialFacilityAggregateSearch } from 'app/ComponentLibrary/Filter/FacilitySearch/Index';
import { initialUnitAggregateSearch } from 'app/ComponentLibrary/Filter/UnitSearch/Index';
import { Collapse, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { SearchPageIcons } from 'app/ComponentLibrary/Filter/SearchPageIcons';
import { isEqual } from 'lodash';
import { determineChipSelected } from 'app/components/AmWorkDesk/PlacementsDesk/filterHelper';
import { instanceOfAggregateUnit } from 'app/ComponentLibrary/Filter/UnitSearch/Index';
import { instanceOfAggregateCandidate } from 'app/ComponentLibrary/Filter/CandidateSearch';
import i18next from 'i18next';
import { selectUser } from 'oidc/user.selectors';
import { reconcileSelfEmployeeNameInValues } from 'oidc/selfEmployeeNameReconciler';

const useStyles = makeStyles()({
  fillterCollapse: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
});

/** Format unit chips on /search/tasks as "Unit: [Unit Name] ([Unit ID])". */
function formatUnitChipLabels(chips: Record<string, any[]>): Record<string, any[]> {
  const unitKey = TaskSearchFilterName.unit;
  const unitChips = chips?.[unitKey];
  if (!Array.isArray(unitChips)) return chips;
  const unitLabel = i18next.t('unitFilter.unit');
  const formatted = { ...chips };
  formatted[unitKey] = unitChips.map(chip => {
    const choice = chip?.value?.value;
    if (choice === undefined || choice === null) return chip;
    // choice may be a primitive (UID only) or an object { unitId, unitName, value, ... }
    const isObject = typeof choice === 'object';
    const unitName = isObject ? (choice?.unitName ?? choice?.name ?? '') : '';
    const unitId = isObject
      ? (choice?.value ?? choice?.unitId ?? choice?.id ?? '')
      : String(choice);
    const prefix = unitLabel.replace(/:+\s*$/, '').trim();
    const label = unitName ? `${prefix}: ${unitName} (${unitId})` : `${prefix}: (${unitId})`;
    return { ...chip, label };
  });
  return formatted;
}

/**
 * Keys that reference the logged-in user via `{ id, value, name }`. Used to
 * fix stale snapshot names on the chip strip. ADO 506080 (Bug 2).
 */
const SELF_EMPLOYEE_FILTER_KEYS: ReadonlyArray<string> = [
  TaskSearchFilterName.recipients,
  TaskSearchFilterName.createdBy,
];

export interface SearchTasksChipsProps {
  onApplyFilter: (values?: any, newSelectedChipFilters?: any) => void;
  hideDelete?: boolean;
  setChipExpanded?: (fn: (prev: boolean) => boolean) => void;
  chipLabelPrefixes?: Record<string, string>;
  /**
   * Tag of the grid that owns this chip strip (e.g. 'GlobalSearchGrid-tasks').
   * When provided, chips are only rendered if the global gridState.filterAttributes
   * was authored for the same grid. Prevents chips from another search page
   * (Candidate, Placement, …) leaking onto the Tasks chip strip during route
   * transitions, Workdesk Placements, or session restore. Filters with no
   * `associatedGridName` (Workdesk) must not render here. See ADO 506080 (Bug 1).
   */
  gridTag?: string;
}

export const SearchTasksChips: React.FC<SearchTasksChipsProps> = props => {
  const { classes } = useStyles();
  const { getValues, setValue } = useFormContext();
  const [chips, setChips] = useState({});
  const filterObject = useSelector(selectFilters);
  const { userInfo } = useSelector(selectUser);
  const [checked, setChecked] = React.useState(false);
  const divRef = useRef<HTMLDivElement>(null);
  const ownsGridFilters =
    !props.gridTag || filterAttributesBelongToGrid(filterObject, props.gridTag);

  const handleChange = () => {
    setChecked(prev => !prev);
    props.setChipExpanded?.(prev => !prev);
  };

  const buildChips = (values, selectedChipFilters = filterObject?.selectedChipFilters) =>
    values
      ? formatUnitChipLabels(
          generateTaskChips(values, !!props.hideDelete, selectedChipFilters, props.chipLabelPrefixes),
        )
      : {};

  const getNewSelection = (key, chip) => ({
    ...filterObject?.selectedChipFilters,
    [key]: determineChipSelected(
      chip,
      filterObject?.selectedChipFilters ? filterObject?.selectedChipFilters[key] : undefined,
    ),
  });

  const handleChipClick = (key, chip) => {
    const fieldValues = getValues(key);
    if (!Array.isArray(fieldValues)) {
      if (
        !instanceOfAggregateCandidate(fieldValues) &&
        !instanceOfAggregateUnit(fieldValues) &&
        fieldValues !== null &&
        typeof fieldValues !== 'object'
      ) {
        return;
      }
      if (
        chip?.value?.key &&
        !Array.isArray(fieldValues?.[chip.value.key]) &&
        !instanceOfAggregateCandidate(fieldValues) &&
        !instanceOfAggregateUnit(fieldValues)
      ) {
        return;
      }
    }
    const newSelections = getNewSelection(key, chip);
    setChips(buildChips(getValues(), newSelections));
    props.onApplyFilter(getValues(), newSelections);
  };

  const handleChipDelete = (field, chip) => {
    const chosenValue = getValues(field);
    if (field === TaskSearchFilterName.candidateAggregate) {
      if (chosenValue && chosenValue?.selections && chosenValue.selections.length > 0) {
        const chipChoice = chip.value?.value;
        const candidateIdToRemove = chipChoice?.value || chipChoice?.id || chipChoice?.candidateId;
        const newSelections = chosenValue.selections.filter(opt => {
          const optValue = opt.value || opt.id || opt.candidateId;
          return optValue != candidateIdToRemove;
        });
        setValue(field, {
          ...chosenValue,
          selections: newSelections,
        });
      } else {
        setValue(field, initialCandidateAggregateSearch);
      }
    } else if (field === TaskSearchFilterName.facilityId) {
      // Mods link-task search (and similar) use TYPEAHEADDROPDOWN → plain { id, value, name }[].
      // Global task search may use FACILITYSEARCH → aggregate with `selections`.
      if (Array.isArray(chosenValue)) {
        const newValues = [...chosenValue];
        const idx = newValues.findIndex(opt =>
          isEqual(instanceOfISelection(chip) ? opt.value : opt, chip.value),
        );
        if (idx !== -1) {
          newValues.splice(idx, 1);
        }
        setValue(field, newValues);
      } else if (chip?.value?.key && chip.value.key !== 'selections') {
        setValue(field, {
          ...chosenValue,
          [chip.value.key]: null,
        });
      } else if (chosenValue && chosenValue?.selections && chosenValue.selections.length > 0) {
        const chipChoice = chip.value?.value;
        const facilityIdToRemove = chipChoice?.value || chipChoice?.id || chipChoice?.fid;
        const newSelections = chosenValue.selections.filter(opt => {
          const optValue = opt.value || opt.id || opt.fid;
          return optValue != facilityIdToRemove;
        });
        setValue(field, {
          ...chosenValue,
          selections: newSelections,
        });
      } else {
        setValue(field, initialFacilityAggregateSearch);
      }
    } else if (field === TaskSearchFilterName.unit) {
      if (chosenValue && chosenValue?.selections && chosenValue.selections.length > 0) {
        const chipChoice = chip.value?.value;
        const unitIdToRemove = chipChoice?.value || chipChoice?.id || chipChoice?.unitId;
        const newSelections = chosenValue.selections.filter(opt => {
          const optValue = opt.value || opt.id || opt.unitId;
          return optValue != unitIdToRemove;
        });
        setValue(field, {
          ...chosenValue,
          selections: newSelections,
        });
      } else {
        setValue(field, initialUnitAggregateSearch);
      }
    } else if (field === 'sentDateRange') {
      setValue('sentDateRange', null);
      setValue(TaskSearchFilterName.sentDateMin, null);
      setValue(TaskSearchFilterName.sentDateMax, null);
    } else if (field === 'followUpDateRange') {
      setValue('followUpDateRange', null);
      setValue(TaskSearchFilterName.followUpDateMin, null);
      setValue(TaskSearchFilterName.followUpDateMax, null);
    } else if (chosenValue && chosenValue?.length > 0 && Array.isArray(chosenValue)) {
      const newValues = [...chosenValue];
      newValues.splice(
        newValues.findIndex(opt => isEqual(instanceOfISelection(chip) ? opt.value : opt, chip.value)),
        1,
      );
      setValue(field, newValues);
    } else {
      if (field === 'dueDate') {
        setValue(field, null);
      } else {
        setValue(field, []);
      }
    }

    const updatedValues = getValues();
    const nextSelectedChipFilters = chip.isSelected ? getNewSelection(field, chip) : undefined;
    setChips(buildChips(updatedValues, nextSelectedChipFilters));
    props.onApplyFilter(updatedValues, nextSelectedChipFilters);
  };

  useEffect(() => {
    if (!ownsGridFilters) {
      setChips({});
      return;
    }
    const valuesForChips = reconcileSelfEmployeeNameInValues(
      filterObject?.filters as Record<string, unknown> | undefined,
      SELF_EMPLOYEE_FILTER_KEYS,
      {
        employeeId: userInfo?.employeeId,
        firstName: userInfo?.firstName,
        lastName: userInfo?.lastName,
      },
    );
    setChips(buildChips(valuesForChips));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ownsGridFilters,
    filterObject?.filters,
    filterObject?.selectedChipFilters,
    userInfo?.employeeId,
    userInfo?.firstName,
    userInfo?.lastName,
  ]);

  return (
    <>
      <Grid container>
        <Grid item xs={11}>
          <Collapse in={checked} collapsedSize={38}>
            <div ref={divRef}>
              <ChipsContainer
                isCollapse={!checked}
                chips={chips}
                onClick={(field, chip) => handleChipClick(field, chip)}
                onDelete={(field, chip) => handleChipDelete(field, chip)}
              />
            </div>
          </Collapse>
        </Grid>
        <Grid item xs={1} className={classes.fillterCollapse}>
          {(divRef?.current?.clientHeight ?? 0) > 38 && (
            <SearchPageIcons KeyboardArrowIcon onAdvancedChipsClick={() => handleChange()} checked={checked} />
          )}
        </Grid>
      </Grid>
    </>
  );
};
